import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Link } from 'react-router';
import { breakpoint, colors, spacing, typography } from '../../../themes/stena-recycling';
import { type StyledButtonProps } from '../../../types';
import { Spinner } from '../../Feedback/Spinner';
import { animationTransition } from '../../helpers/styleHelpers';

const variantTypography = {
  primary: typography.other.button.label,
  secondary: typography.other.button.label,
  tertiary: typography.other.button.label,
  'text-primary': typography.other.button.secondaryLabel,
  'text-secondary': typography.other.button.secondaryLabel,
  'text-primary-red': typography.other.button.secondaryLabel,
};

export const StyledButton = styled.button<StyledButtonProps>`
  font-family: ${typography.fontFamily};
  display: flex;
  border: none;
  user-select: none;
  transition: background-color ${animationTransition};
  height: ${spacing.mediumHigh};
  justify-content: center;
  white-space: nowrap;
  align-items: center;
  cursor: pointer;
  ${({ fullWidth }) => fullWidth && 'width: 100%'};

  ${({ variant }) => (variant && variantTypography[variant]) ?? null}

  ${({ variant, isLoading, hasIcon }) => getButtonVariant(variant, isLoading, hasIcon)}

  ${({ hasIcon, isLoading }) =>
    hasIcon &&
    isLoading &&
    'padding-left: 16px; padding-right: 20px;'} // 24px is the width of the spinner, divide this and add to default padding


   @media (max-width: ${breakpoint.medium}px) {
    white-space: normal;
  }

  ${({ underlined }) =>
    underlined &&
    css`
      text-decoration: underline;
    `}
`;

export const Label = styled.span<
  {
    isLoading?: boolean;
    icon: boolean;
    iconPlacement?: 'before' | 'after';
  } & Pick<StyledButtonProps, 'variant'>
>`
  opacity: ${({ isLoading }) => (isLoading ? 0 : 1)};
  color: inherit;
  color: ${({ color }) => color ?? 'inherit'};

  ${({ icon, iconPlacement, variant }) => {
    if (icon && iconPlacement !== 'after') {
      return css`
        margin-left: ${
          variant &&
          (variant === 'text-primary' ||
            variant === 'text-secondary' ||
            variant === 'text-primary-red')
            ? spacing.tiny
            : spacing.xxsmall
        };
      `;
    }

    if (icon && iconPlacement === 'after') {
      return css`
        margin-right: ${
          variant &&
          (variant === 'text-primary' ||
            variant === 'text-secondary' ||
            variant === 'text-primary-red')
            ? spacing.tiny
            : spacing.xxsmall
        };
      `;
    }
  }}
`;

// Loader
export const ButtonSpinner = styled(Spinner)`
  position: absolute;
`;

// Icons
const iconStyles = css`
  width: ${spacing.small};
  height: ${spacing.small};
  box-sizing: border-box;
`;

export const IconLeft = styled.div`
  color: inherit;
  ${iconStyles}
`;

export const IconRight = styled.div`
  color: inherit;
  ${iconStyles}
`;

const getButtonVariant = (
  variant?:
    'primary' | 'secondary' | 'tertiary' | 'text-primary' | 'text-secondary' | 'text-primary-red',
  isLoading?: boolean,
  hasIcon?: boolean,
) => {
  switch (variant) {
    case 'secondary':
      return css`
        height: ${spacing.mediumHigh};
        padding: ${spacing.xmsmall} ${spacing.mediumLow} ${spacing.xmsmall} ${spacing.small};
        background-color: ${colors.base.white};
        color: ${colors.primary.blue};
        border: 1px solid ${colors.primary.blue};
        box-sizing: border-box;

        &:disabled {
          color: ${isLoading ? colors.primary.blue : colors.monochrome.grey40};
          border: 1px solid ${colors.monochrome.grey20};
          pointer-events: none;
        }

        &:hover:not(:disabled) {
          border: 1px solid ${colors.primary.blue};
          background-color: ${colors.primary.blueTint4};
          color: ${colors.primary.blue};
        }

        &:active:not(:disabled) {
          border: 1px solid ${colors.primary.blue};
          background-color: ${colors.primary.blueTint3};
          color: ${colors.primary.blue};
        }
      `;
    case 'tertiary':
      return css`
        height: auto;
        padding: 0;
        background-color: transparent;
        color: ${colors.primary.blue};

        &:disabled {
          color: ${isLoading ? colors.primary.blue : colors.monochrome.grey40};
          pointer-events: none;
        }

        &:hover:not(:disabled) {
          color: ${colors.primary.blueShade1};
        }

        &:active:not(:disabled) {
          color: ${colors.primary.blueShade2};
        }
      `;

    case 'text-primary':
      return css`
        height: auto;
        padding: ${spacing.tiny} ${spacing.xxsmall} ${spacing.tiny}
          ${hasIcon ? spacing.tiny : spacing.xxsmall};
        background-color: transparent;
        color: ${colors.primary.blue};
        border: 1px solid ${colors.primary.blue};
        border-radius: 4px;
        box-sizing: border-box;

        &:disabled {
          color: ${isLoading ? colors.primary.blue : colors.monochrome.grey40};
          border: 1px solid ${colors.monochrome.grey20};
          pointer-events: none;
        }

        &:hover:not(:disabled) {
          border: 1px solid ${colors.primary.blueShade1};
          color: ${colors.primary.blueShade1};
        }

        &:active:not(:disabled) {
          border: 1px solid ${colors.primary.blueShade2};
          color: ${colors.primary.blueShade2};
        }
      `;

    case 'text-primary-red':
      return css`
        height: auto;
        padding: ${spacing.tiny} ${spacing.xxsmall} ${spacing.tiny}
          ${hasIcon ? spacing.tiny : spacing.xxsmall};
        background-color: transparent;
        color: ${colors.alerts.error};
        border: 1px solid ${colors.alerts.error};
        border-radius: 4px;
        box-sizing: border-box;

        &:disabled {
          color: ${isLoading ? colors.alerts.error : colors.monochrome.grey40};
          border: 1px solid ${colors.monochrome.grey20};
          pointer-events: none;
        }

        &:hover:not(:disabled) {
          border: 1px solid ${colors.alerts.errorShade1};
          color: ${colors.alerts.errorShade1};
        }

        &:active:not(:disabled) {
          border: 1px solid ${colors.alerts.errorShade2};
          color: ${colors.alerts.errorShade2};
        }
      `;

    case 'text-secondary':
      return css`
        height: auto;
        padding: 0;
        background-color: transparent;
        color: ${colors.primary.blue};
        border-radius: 4px;
        box-sizing: border-box;

        &:disabled {
          color: ${isLoading ? colors.primary.blue : colors.monochrome.grey40};
          pointer-events: none;
        }

        &:hover:not(:disabled) {
          color: ${colors.primary.blueShade1};
        }

        &:active:not(:disabled) {
          color: ${colors.primary.blueShade2};
        }
      `;

    case 'primary':
    default:
      return css`
        height: ${spacing.mediumHigh};
        padding: ${spacing.xmsmall} ${spacing.mediumLow};
        background-color: ${colors.primary.blue};
        color: ${colors.base.white};

        &:disabled {
          background-color: ${colors.monochrome.grey20};
          color: ${isLoading ? colors.primary.blue : colors.monochrome.grey40};
          pointer-events: none;
        }

        &:hover:not(:disabled) {
          background-color: ${colors.primary.blueShade1};
        }

        &:active:not(:disabled) {
          background-color: ${colors.primary.blueShade2};
        }
      `;
  }
};

export const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'fullWidth' && prop !== 'isLoading' && prop !== 'hasIcon',
})<StyledButtonProps>`
  font-family: ${typography.fontFamily};
  display: inline-flex;
  border: none;
  user-select: none;
  transition: background-color ${animationTransition};
  height: ${spacing.mediumHigh};
  justify-content: center;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  ${({ fullWidth }) => fullWidth && 'width: 100%'};

  ${({ variant }) => (variant && variantTypography[variant]) ?? null}

  ${({ variant, isLoading, hasIcon }) => getButtonVariant(variant, isLoading, hasIcon)}

  ${({ hasIcon, isLoading }) =>
    hasIcon &&
    isLoading &&
    'padding-left: 16px; padding-right: 20px;'} // 24px is the width of the spinner, divide this and add to default padding
`;
